import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api.service';
import { usePermissions } from '../../context/PermissionsContext';
import {
  formatMoney,
  formatDateTime,
  humanizeTreasuryError,
} from '../treasury/treasuryUi';
import { useCashSession } from '../../components/CashSessionBar';

const CLOSE_EVENT = 'cash-session-close-request';

type Step = 1 | 2 | 3 | 4 | 5;

function isInflow(type: string) {
  return ['IN', 'INCOME', 'TRANSFER_IN', 'ADJUSTMENT_IN', 'OPENING', 'REVERSAL'].includes(type);
}

function classifyMovement(m: any): string {
  const src = String(m.sourceType ?? '').toUpperCase();
  const type = String(m.type ?? '');
  if (m.paymentId || src === 'PAYMENT' || src.includes('PAYMENT')) return 'COBRO';
  if (src.includes('DISBURSE') || m.loanDisbursement || (m.loanId && !m.paymentId && !isInflow(type))) {
    return 'DESEMBOLSO';
  }
  if (type === 'TRANSFER_IN' || (src === 'CASH_TRANSFER' && isInflow(type))) {
    return 'TRANSFERENCIA RECIBIDA';
  }
  if (type === 'TRANSFER_OUT' || (src === 'CASH_TRANSFER' && !isInflow(type))) {
    return 'TRANSFERENCIA ENVIADA';
  }
  if (m.adjustmentId || src.includes('ADJUST')) return 'AJUSTE';
  if (isInflow(type)) return 'INGRESO';
  return 'EGRESO';
}

function diffLabel(diff: number) {
  if (Math.abs(diff) < 0.01) return { code: 'SIN_DIFERENCIA' as const, label: 'Sin diferencia' };
  if (diff > 0) return { code: 'SOBRANTE' as const, label: 'Sobrante' };
  return { code: 'FALTANTE' as const, label: 'Faltante' };
}

/**
 * TX7 — wizard único de cierre (+ arqueo si el rol puede auditar).
 * Escucha el mismo evento que Mi caja / barra.
 */
export function CloseCashWizard() {
  const { session, refresh } = useCashSession();
  const { session: permSession } = usePermissions();
  const canRunAudit = ['ADMIN', 'MANAGER', 'ACCOUNTANT'].includes(permSession?.user.role ?? '');

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmModal, setConfirmModal] = useState(false);

  const [systemBalance, setSystemBalance] = useState<number | null>(null);
  const [summary, setSummary] = useState({
    income: 0,
    expense: 0,
    collections: 0,
    disbursements: 0,
    transferIn: 0,
    transferOut: 0,
    other: 0,
  });

  const [counted, setCounted] = useState('');
  const [showDenoms, setShowDenoms] = useState(false);
  const [denominations, setDenominations] = useState<number[]>([]);
  const [denomCounts, setDenomCounts] = useState<Record<number, string>>({});
  const [reason, setReason] = useState('');

  const [result, setResult] = useState<{
    closed: any;
    audit: any | null;
    counted: number;
    system: number;
    difference: number;
  } | null>(null);

  const loadSummary = useCallback(async (sessionId: string) => {
    const [bal, movs] = await Promise.all([
      apiService.getCashSessionBalance(sessionId),
      apiService.listCashMovements({ sessionId }),
    ]);
    setSystemBalance(Number(bal.systemBalance ?? 0));
    let income = 0;
    let expense = 0;
    let collections = 0;
    let disbursements = 0;
    let transferIn = 0;
    let transferOut = 0;
    let other = 0;
    for (const m of movs ?? []) {
      const amt = Number(m.amount) || 0;
      const badge = classifyMovement(m);
      if (badge === 'COBRO') collections += amt;
      else if (badge === 'DESEMBOLSO') disbursements += amt;
      else if (badge === 'TRANSFERENCIA RECIBIDA') transferIn += amt;
      else if (badge === 'TRANSFERENCIA ENVIADA') transferOut += amt;
      else if (badge === 'AJUSTE') other += amt;
      else if (isInflow(m.type)) income += amt;
      else expense += amt;
    }
    setSummary({ income, expense, collections, disbursements, transferIn, transferOut, other });
  }, []);

  useEffect(() => {
    const onCloseRequest = () => {
      if (!session?.id) return;
      setError('');
      setStep(1);
      setResult(null);
      setConfirmModal(false);
      setReason('');
      setDenomCounts({});
      setShowDenoms(false);
      const sys =
        session.systemBalance != null ? Number(session.systemBalance) : null;
      setCounted(sys != null ? String(sys) : '');
      setOpen(true);
      void loadSummary(session.id).catch((e: any) =>
        setError(humanizeTreasuryError(e?.response?.data?.message || e.message)),
      );
    };
    window.addEventListener(CLOSE_EVENT, onCloseRequest);
    return () => window.removeEventListener(CLOSE_EVENT, onCloseRequest);
  }, [session, loadSummary]);

  useEffect(() => {
    if (!open) return;
    apiService
      .getCashDenominations()
      .then((d) => setDenominations((d ?? []).map((x: any) => x.denomination)))
      .catch(() => setDenominations([]));
  }, [open]);

  const denomTotal = useMemo(() => {
    return denominations.reduce((sum, d) => {
      const q = parseInt(denomCounts[d] ?? '0', 10) || 0;
      return sum + d * q;
    }, 0);
  }, [denominations, denomCounts]);

  useEffect(() => {
    if (showDenoms && denomTotal > 0) {
      setCounted(String(Math.round(denomTotal * 100) / 100));
    }
  }, [showDenoms, denomTotal]);

  const countedNum = parseFloat(counted);
  const countedValid = counted !== '' && !Number.isNaN(countedNum) && countedNum >= 0;
  const sys = systemBalance ?? (session?.systemBalance != null ? Number(session.systemBalance) : 0);
  const difference = countedValid ? Math.round((countedNum - sys) * 100) / 100 : 0;
  const klass = diffLabel(difference);
  const needsReason = Math.abs(difference) >= 0.01;

  const denominationPayload = useMemo(() => {
    if (!showDenoms) return undefined;
    const rows = denominations
      .map((d) => ({ denomination: d, quantity: parseInt(denomCounts[d] ?? '0', 10) || 0 }))
      .filter((x) => x.quantity > 0);
    return rows.length ? rows : undefined;
  }, [showDenoms, denominations, denomCounts]);

  const closeWizard = () => {
    setOpen(false);
    setConfirmModal(false);
    setResult(null);
    setError('');
  };

  const runClose = async () => {
    if (!session?.id || !countedValid) return;
    if (needsReason && !reason.trim()) {
      setError('Indicá el motivo de la diferencia.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const closed = await apiService.closeCashSession(
        session.id,
        countedNum,
        reason.trim() || undefined,
      );
      const system = Number(closed.systemBalance ?? sys);
      const diff = Math.round((countedNum - system) * 100) / 100;
      let audit: any = null;
      if (canRunAudit) {
        try {
          audit = await apiService.auditCashSession(closed.id, {
            countedAmount: countedNum,
            notes: reason.trim() || undefined,
            denominations: denominationPayload,
          });
        } catch (auditErr: any) {
          // Cierre ok; arqueo queda pendiente
          setError(
            `Caja cerrada. El arqueo quedó pendiente: ${
              auditErr?.response?.data?.message || auditErr.message
            }`,
          );
        }
      }
      await refresh();
      setResult({
        closed,
        audit,
        counted: countedNum,
        system,
        difference: diff,
      });
      setConfirmModal(false);
      setStep(5);
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setError(humanizeTreasuryError(Array.isArray(msg) ? msg.join(' ') : msg || e.message));
      setConfirmModal(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !session) return null;

  const cajaLabel =
    session.cashRegister?.number != null
      ? `Caja ${session.cashRegister.number}`
      : session.cashRegister?.name || 'Caja';
  const branchLabel = session.cashRegister?.branch?.name ?? '—';
  const operator =
    [session.user?.firstName, session.user?.lastName].filter(Boolean).join(' ') ||
    session.user?.email ||
    '—';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4 my-8">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Mi caja</p>
              <h2 className="text-lg font-semibold text-gray-900">
                {step === 5 ? 'Caja cerrada' : 'Cerrar caja'}
              </h2>
              <p className="text-sm text-gray-600">
                {branchLabel} · {cajaLabel}
              </p>
            </div>
            <button
              type="button"
              onClick={closeWizard}
              className="text-sm text-slate-500 hover:text-slate-800"
            >
              Cerrar
            </button>
          </div>

          {step < 5 && (
            <ol className="flex flex-wrap gap-2 text-xs">
              {[
                [1, 'Resumen'],
                [2, 'Conteo'],
                [3, 'Diferencias'],
                [4, 'Confirmación'],
              ].map(([n, label]) => (
                <li
                  key={n}
                  className={`px-2.5 py-1 rounded-full ${
                    step === n
                      ? 'bg-slate-900 text-white'
                      : step > (n as number)
                        ? 'bg-slate-200 text-slate-700'
                        : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {label}
                </li>
              ))}
            </ol>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 border p-3">
                <div className="text-xs text-slate-500">Saldo sistema</div>
                <div className="text-xl font-semibold tabular-nums">{formatMoney(sys)}</div>
              </div>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                {[
                  ['Ingresos', summary.income],
                  ['Egresos', summary.expense],
                  ['Cobros', summary.collections],
                  ['Desembolsos', summary.disbursements],
                  ['Transferencias recibidas', summary.transferIn],
                  ['Transferencias enviadas', summary.transferOut],
                  ['Otros movimientos', summary.other],
                ].map(([label, val]) => (
                  <div key={String(label)} className="border rounded-md px-3 py-2">
                    <dt className="text-xs text-slate-500">{label}</dt>
                    <dd className="font-medium tabular-nums">{formatMoney(Number(val))}</dd>
                  </div>
                ))}
              </dl>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeWizard}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-4 py-2 bg-slate-900 text-white rounded-md"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto contado
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  autoFocus
                  value={counted}
                  onChange={(e) => setCounted(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Efectivo contado ahora"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={showDenoms}
                  onChange={(e) => setShowDenoms(e.target.checked)}
                />
                Desglose por denominación (opcional)
              </label>
              {showDenoms && (
                <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                  <div className="grid grid-cols-3 gap-2 text-xs text-slate-500 font-medium">
                    <span>Denominación</span>
                    <span>Cantidad</span>
                    <span>Subtotal</span>
                  </div>
                  {denominations.map((d) => {
                    const q = parseInt(denomCounts[d] ?? '0', 10) || 0;
                    return (
                      <div key={d} className="grid grid-cols-3 gap-2 items-center text-sm">
                        <span className="tabular-nums">{formatMoney(d)}</span>
                        <input
                          type="number"
                          min={0}
                          value={denomCounts[d] ?? ''}
                          onChange={(e) =>
                            setDenomCounts({ ...denomCounts, [d]: e.target.value })
                          }
                          className="px-2 py-1 border rounded"
                        />
                        <span className="tabular-nums text-slate-600">{formatMoney(d * q)}</span>
                      </div>
                    );
                  })}
                  <p className="text-sm font-medium pt-1">
                    Total contado: {formatMoney(denomTotal)}
                  </p>
                </div>
              )}
              <div className="flex justify-between gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Atrás
                </button>
                <button
                  type="button"
                  disabled={!countedValid}
                  onClick={() => setStep(3)}
                  className="px-4 py-2 bg-slate-900 text-white rounded-md disabled:opacity-50"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-slate-500">Saldo sistema</dt>
                  <dd className="font-medium tabular-nums">{formatMoney(sys)}</dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-slate-500">Total contado</dt>
                  <dd className="font-medium tabular-nums">{formatMoney(countedNum)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Diferencia</dt>
                  <dd
                    className={`font-semibold tabular-nums ${
                      klass.code === 'SIN_DIFERENCIA'
                        ? 'text-green-700'
                        : klass.code === 'SOBRANTE'
                          ? 'text-amber-700'
                          : 'text-red-700'
                    }`}
                  >
                    {formatMoney(difference)} · {klass.label}
                  </dd>
                </div>
              </dl>
              {needsReason && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo de la diferencia <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Explicá el sobrante o faltante…"
                  />
                </div>
              )}
              <div className="flex justify-between gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Atrás
                </button>
                <button
                  type="button"
                  disabled={needsReason && !reason.trim()}
                  onClick={() => setStep(4)}
                  className="px-4 py-2 bg-slate-900 text-white rounded-md disabled:opacity-50"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <div className="rounded-md border bg-slate-50 p-3 text-sm space-y-1">
                <p>
                  <span className="text-slate-500">Saldo sistema:</span> {formatMoney(sys)}
                </p>
                <p>
                  <span className="text-slate-500">Contado:</span> {formatMoney(countedNum)}
                </p>
                <p>
                  <span className="text-slate-500">Diferencia:</span> {formatMoney(difference)} (
                  {klass.label})
                </p>
                {reason.trim() && (
                  <p>
                    <span className="text-slate-500">Motivo:</span> {reason.trim()}
                  </p>
                )}
                {canRunAudit && (
                  <p className="text-xs text-slate-500 pt-1">
                    Al confirmar se registra también el arqueo documental.
                  </p>
                )}
                {!canRunAudit && (
                  <p className="text-xs text-amber-800 pt-1">
                    El arqueo quedará pendiente para supervisión.
                  </p>
                )}
              </div>
              <div className="flex justify-between gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Atrás
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setConfirmModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Cerrar caja
                </button>
              </div>
            </div>
          )}

          {step === 5 && result && (
            <div className="space-y-3">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm space-y-2">
                <p className="font-semibold text-emerald-900">Caja cerrada</p>
                <p>
                  Saldo final (sistema): <strong>{formatMoney(result.system)}</strong>
                </p>
                <p>
                  Contado: <strong>{formatMoney(result.counted)}</strong>
                </p>
                <p>
                  Diferencia:{' '}
                  <strong>{formatMoney(result.difference)}</strong> ·{' '}
                  {diffLabel(result.difference).label}
                </p>
                <p>
                  Fecha/hora:{' '}
                  <strong>
                    {formatDateTime(result.closed.closedAt || new Date())}
                  </strong>
                </p>
                <p>
                  Operador: <strong>{operator}</strong>
                </p>
                {result.audit ? (
                  <p className="text-emerald-800">Arqueo registrado.</p>
                ) : (
                  <p className="text-amber-900">Arqueo pendiente de supervisión.</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Link
                  to={
                    result.audit
                      ? '/treasury/supervision?tab=arqueos'
                      : '/treasury/supervision?tab=pending'
                  }
                  className="px-4 py-2 text-sm border rounded-md hover:bg-slate-50"
                  onClick={closeWizard}
                >
                  Ver arqueo
                </Link>
                <button
                  type="button"
                  onClick={closeWizard}
                  className="px-4 py-2 text-sm bg-slate-900 text-white rounded-md"
                >
                  Listo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">¿Cerrar la caja?</h3>
            <p className="text-sm text-gray-600">
              Se cerrará la sesión con contado {formatMoney(countedNum)}.
              {needsReason &&
                ' La diferencia se registrará contablemente al cierre según el sistema.'}
              Esta acción no se puede deshacer desde acá.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Volver
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void runClose()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? 'Cerrando…' : 'Confirmar cierre'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
